/**
 * ============================================
 * Mann-Kendall 趋势检验 - 详细教程和 API 示例
 * ============================================
 *
 * Mann-Kendall 检验是一种非参数统计方法，用于检测时间序列数据是否存在单调趋势
 * （上升或下降趋势）。它不需要假设数据服从特定分布，适用于各种类型的数据。
 *
 * 基本原理：
 * 1. 零假设 (H₀): 数据不存在趋势（随机序列）
 * 2. 备择假设 (H₁): 数据存在单调趋势（上升或下降）
 * 3. 通过计算 S 统计量、方差和标准化 Z 值来判断趋势
 */

// 注意：由于 npm 上没有现成的 ml-isotonic-regression 包，我们自行实现了该功能

/**
 * Mann-Kendall 趋势检验函数
 *
 * @param {Array<number>} data - 时间序列数据数组
 * @param {number} alpha - 显著性水平，默认 0.05
 * @returns {Object} 检验结果对象
 * @property {number} S - Mann-Kendall 统计量
 * @property {number} varS - S 统计量的方差
 * @property {number} Z - 标准化检验统计量
 * @property {string} trend - 趋势方向: 'increasing', 'decreasing', 'no trend'
 * @property {number} pValue - p 值（近似）
 */
function mannKendall(data, alpha = 0.05) {
  if (!Array.isArray(data) || data.length < 3) {
    return {}
    // throw new Error('数据必须是至少包含3个元素的数组');
  }

  const n = data.length;
  let S = 0;

  // 计算 S 统计量：比较所有数据对
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 1; j < n; j++) {
      const diff = data[j] - data[i];
      if (diff > 0) {
        S += 1;
      } else if (diff < 0) {
        S -= 1;
      }
      // diff === 0 时，S 不变（ties）
    }
  }

  // 计算重复值（ties）的数量
  const valueCounts = {};
  data.forEach(val => {
    valueCounts[val] = (valueCounts[val] || 0) + 1;
  });

  // 计算 ties 的修正项
  let tieCorrection = 0;
  Object.values(valueCounts).forEach(count => {
    if (count > 1) {
      tieCorrection += count * (count - 1) * (2 * count + 5);
    }
  });

  // 计算 S 的方差（考虑 ties）
  const varS = (n * (n - 1) * (2 * n + 5) - tieCorrection) / 18;

  // 计算标准化统计量 Z
  let Z;
  if (S > 0) {
    Z = (S - 1) / Math.sqrt(varS);
  } else if (S < 0) {
    Z = (S + 1) / Math.sqrt(varS);
  } else {
    Z = 0;
  }

  // 确定趋势方向
  let trend = 'no trend';
  const criticalValue = getCriticalValue(alpha);

  if (Z > criticalValue) {
    trend = 'increasing';
  } else if (Z < -criticalValue) {
    trend = 'decreasing';
  }

  // 计算近似的 p 值（使用标准正态分布）
  const pValue = 2 * (1 - normalCDF(Math.abs(Z)));

  return {
    S,
    varS,
    Z: Number(Z.toFixed(2)),
    trend,
    pValue: Number(pValue.toFixed(4))
  };
}

/**
 * 获取临界值（基于显著性水平）
 */
function getCriticalValue(alpha) {
  const criticalValues = {
    0.001: 3.29,
    0.01: 2.58,
    0.05: 1.96,
    0.10: 1.65
  };

  // 找到最接近的显著性水平
  const levels = Object.keys(criticalValues).map(Number).sort((a, b) => b - a);
  for (const level of levels) {
    if (alpha >= level) {
      return criticalValues[level];
    }
  }
  return criticalValues[0.05]; // 默认
}

/**
 * 标准正态分布的累积分布函数（CDF）近似
 * 使用误差函数（erf）的近似公式
 */
function normalCDF(x) {
  // 使用 Abramowitz and Stegun 近似公式
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.sqrt(2.0);

  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

  return 0.5 * (1.0 + sign * y);
}
// 注意：由于 npm 上没有现成的 ml-isotonic-regression 包，我们自行实现了该功能

// ============================================
// API 返回值说明
// ============================================
/**
 * mannKendall(data) 返回一个对象，包含以下属性：
 *
 * @param {Array<number>} data - 时间序列数据数组
 * @returns {Object} 检验结果对象
 * @property {number} S - Mann-Kendall 统计量
 *   - S > 0: 表示上升趋势
 *   - S < 0: 表示下降趋势
 *   - S = 0: 表示无趋势
 *
 * @property {number} varS - S 统计量的方差
 *   - 用于计算标准化统计量 Z
 *   - 考虑了数据中的重复值（ties）
 *
 * @property {number} Z - 标准化检验统计量
 *   - Z = S / √varS
 *   - |Z| > 1.96 (α=0.05): 显著趋势
 *   - |Z| > 2.58 (α=0.01): 极显著趋势
 *   - |Z| < 1.96: 无显著趋势
 *
 * @property {string} trend - 趋势方向
 *   - 'increasing': 上升趋势
 *   - 'decreasing': 下降趋势
 *   - 'no trend': 无趋势
 *
 * @property {number} pValue - p 值（近似）
 *   - p < 0.01: 极显著趋势
 *   - p < 0.05: 显著趋势
 *   - p < 0.10: 弱显著趋势
 *   - p >= 0.10: 无显著趋势
 */


// ============================================
// 结果解释和判断标准
// ============================================
/**
 * 如何解读结果：
 *
 * 1. 查看 trend 属性：
 *    - 'increasing': 数据存在上升趋势
 *    - 'decreasing': 数据存在下降趋势
 *    - 'no trend': 数据无明显趋势
 *
 * 2. 查看 Z 值判断显著性（双侧检验）：
 *    - |Z| > 2.58: 极显著趋势 (p < 0.01)
 *    - |Z| > 1.96: 显著趋势 (p < 0.05)
 *    - |Z| > 1.65: 弱显著趋势 (p < 0.10)
 *    - |Z| < 1.65: 无显著趋势
 *
 * 3. 查看 S 值：
 *    - S 的绝对值越大，趋势越明显
 *    - S 的正负号表示趋势方向
 */

/**
 * 辅助函数：判断趋势显著性
 * @param {Object} result - mannKendall 返回的结果对象
 * @returns {Object} 包含显著性解释的结果对象
 */
function interpretMannKendall(result) {
  const { Z, trend } = result;
  const absZ = Math.abs(Z);

  let significance = '';
  if (absZ > 2.58) {
    significance = '极显著';
  } else if (absZ > 1.96) {
    significance = '显著';
  } else if (absZ > 1.65) {
    significance = '弱显著';
  } else {
    significance = '不显著';
  }

  return {
    ...result,
    significance,
    interpretation: `${trend === 'increasing' ? '上升' : trend === 'decreasing' ? '下降' : '无'}趋势，${significance} (|Z| = ${absZ.toFixed(2)})`
  };
}

// ============================================
// 注意事项
// ============================================
/**
 * 使用 Mann-Kendall 检验的注意事项：
 *
 * 1. 数据要求：
 *    - 数据应该是时间序列（按时间顺序排列）
 *    - 数据点之间应该相互独立
 *    - 至少需要 3 个数据点
 *
 * 2. 适用场景：
 *    - 环境监测数据（温度、降水、污染物浓度等）
 *    - 经济数据（GDP、价格、销量等）
 *    - 生物医学数据（疾病发病率、死亡率等）
 *    - 水文数据（水位、流量等）
 *
 * 3. 局限性：
 *    - 对于存在季节性变化的数据，可能需要季节性 Mann-Kendall 检验
 *    - 对于存在自相关的数据，结果可能不够准确
 *    - 只能检测单调趋势，不能检测周期性变化
 *
 * 4. 与其他方法结合：
 *    - 可以结合 Sen's 斜率估计来量化趋势的强度
 *    - 可以结合 Pettitt 检验来检测突变点
 */

// ============================================
// Isotonic Regression (保序回归)
// ============================================

/**
 * Isotonic Regression (保序回归) 类
 * 用于拟合单调递增或递减的曲线
 */
class IsotonicRegression {
  /**
   * 拟合数据，返回单调序列
   * @param {Array<number>} data - 输入数据
   * @param {boolean} increasing - 是否强制递增，默认 true
   * @returns {Array<number>} 拟合后的单调序列
   */
  fit(data, increasing = true) {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('数据必须是非空数组');
    }

    const n = data.length;
    const result = [...data];

    // 使用 PAVA (Pool Adjacent Violators Algorithm) 算法
    // 这是一个简单的实现，确保结果单调
    if (increasing) {
      // 确保单调递增
      for (let i = 1; i < n; i++) {
        if (result[i] < result[i - 1]) {
          // 如果违反单调性，取平均值
          let j = i;
          let sum = result[i - 1];
          let count = 1;

          // 找到所有需要调整的点
          while (j < n && result[j] < result[i - 1]) {
            sum += result[j];
            count++;
            j++;
          }

          const avg = sum / count;
          // 将这一段都设为平均值
          for (let k = i - 1; k < j; k++) {
            result[k] = avg;
          }
          i = j - 1;
        }
      }
    } else {
      // 确保单调递减
      for (let i = 1; i < n; i++) {
        if (result[i] > result[i - 1]) {
          let j = i;
          let sum = result[i - 1];
          let count = 1;

          while (j < n && result[j] > result[i - 1]) {
            sum += result[j];
            count++;
            j++;
          }

          const avg = sum / count;
          for (let k = i - 1; k < j; k++) {
            result[k] = avg;
          }
          i = j - 1;
        }
      }
    }

    return result;
  }

  /**
   * 预测新值（基于拟合的单调序列）
   * @param {Array<number>} fittedData - 已拟合的数据
   * @param {number} x - 要预测的索引位置
   * @returns {number} 预测值
   */
  predict(fittedData, x) {
    if (x < 0) return fittedData[0];
    if (x >= fittedData.length) return fittedData[fittedData.length - 1];

    const lower = Math.floor(x);
    const upper = Math.ceil(x);

    if (lower === upper) {
      return fittedData[lower];
    }

    // 线性插值
    return fittedData[lower] + (fittedData[upper] - fittedData[lower]) * (x - lower);
  }
}

/**
 * Isotonic Regression 与 Mann-Kendall 的关系：
 * - Mann-Kendall 用于检测是否存在趋势
 * - Isotonic Regression 用于拟合单调趋势曲线
 * - 两者结合可以更好地分析和展示数据趋势
 */

// 导出函数和类
export { mannKendall, IsotonicRegression, interpretMannKendall };

